import React, { useState, useEffect, useMemo } from 'react'
import CustomText from 'components/atoms/CustomText'
import { purpleDark, purple, red, grayText } from '@qirapagos/lib/theme/colors'
import { useAppSelector } from 'hooks/redux'
import { useDispatch } from 'react-redux'
import { getUserBalance } from 'store/transfers/thunks'
import { getHoldingAccount, getFund, getOrders } from 'store/investments/thunks'
import { isWithinInvestmentHours } from 'utils/investmentHours'
import { InvestmentActionType, InvestmentStatus } from '../types'
import { InvestmentOrderType } from 'store/investments/types'
import InvestmentsOutOfService from '../InvestmentsOutOfService'
import { useLocation, useNavigate } from 'react-router-dom'
import {
  MainContainer,
  TitleContainer,
  AlertContainer,
  WarningIcon,
  ContentContainer,
  BalanceSection,
  BalanceInfo,
  AmountInputSection,
  AmountDisplay,
  RescueAllButton,
  DisclaimerSection,
  DisclaimerText,
  CheckboxSection,
  CheckboxContainer,
  Checkbox,
  ButtonSection,
  SubmitButton,
  styles
} from './styles'
import InvestmentOrderSkeleton from 'assets/skeletons/InvestmentOrderSkeleton'
import { InvestmentServices } from 'services'

const OperationInvestment: React.FC = () => {
  const location = useLocation()
  const { balance, isLoading } = useAppSelector(state => state.transfers)
  const {
    investmentHolidays,
    investmentHours,
    fund,
    balanceHoldingAccount
  } = useAppSelector(state => state.investments)
  const dispatch = useDispatch()
  const [initialLoading, setInitialLoading] = useState(true)
  const [amount, setAmount] = useState<string>('')
  const [isRegulationAccepted, setIsRegulationAccepted] = useState<boolean>(false)
  const [hasError, setHasError] = useState<boolean>(false)
  const [errorMessage, setErrorMessage] = useState<string>('')
  const [loadingTerms, setLoadingTerms] = useState(false)

  const navigationState = (location.state as { title?: string }) || {}
  const isRescue = navigationState.title === 'Rescates'
  const actionType = isRescue ? 'rescue' : 'subscription'

  useEffect(() => {
    dispatch(getOrders())
  }, [dispatch])

  const navigate = useNavigate()
  const { investmentClient, orders } = useAppSelector(state => state.investments)

  const FORMAT_ERROR = 'Formato inválido. Ej: 261.55.'
  const HIGHER_ERROR = isRescue
    ? 'El importe es superior a tu saldo invertido'
    : 'El importe es superior a tu saldo disponible'
  const minimumAmount = Number(fund?.minimumAmount)
  const LOWER_ERROR = `El importe no puede ser menor a $${minimumAmount}`

  const isWithinHours = isWithinInvestmentHours(
    investmentHolidays,
    investmentHours,
    actionType
  )

  const subscriptionHoursText = investmentHours?.subscription?.join(' a ') || ''
  const rescueHoursText = investmentHours?.rescue?.join(' a ') || ''
  const subscriptionCloseHour = investmentHours?.subscription?.[1] || ''
  const rescueCloseHour = investmentHours?.rescue?.[1] || ''
  const operationHoursText = isRescue ? rescueHoursText : subscriptionHoursText
  const operationCloseHour = isRescue ? rescueCloseHour : subscriptionCloseHour

  const pendingRescueAmount = useMemo(() => {
    if (!isRescue || !orders) return 0
    const rescues = orders.filter(
      o => o.type === InvestmentOrderType.RESCUE &&
      [InvestmentStatus.PENDING, InvestmentStatus.IN_PROGRESS].includes(o.statusId)
    )
    const toNumber = (val: unknown): number => {
      if (val === null || val === undefined) return 0
      if (typeof val === 'number' && Number.isFinite(val)) return val
      const cleaned = String(val)
        .replace(/\./g, '')
        .replace(/,/g, '.')
        .trim()
      const n = Number(cleaned)
      return Number.isFinite(n) ? n : 0
    }
    const parsed = rescues.map(r => ({
      id: r.id,
      statusId: r.statusId,
      raw: r.amount,
      num: toNumber(r.amount)
    }))
    const totalRaw = parsed.reduce((acc, curr) => acc + curr.num, 0)
    const total = Number(totalRaw.toFixed(2))
    return total
  }, [orders, isRescue])

  const baseOperationPhrase = operationHoursText
    ? `de Lunes a Viernes de ${operationHoursText} h`
    : `hasta las ${operationCloseHour || '—'} horas del corriente día`

  const rescueInstruction =
    `Esta operación puede realizarse ${baseOperationPhrase}.` +
    '\n' +
    'Recibirás el dinero al siguiente día hábil en tu saldo QIRA.'
  const subscriptionInstruction =
    `Esta operación puede realizarse ${baseOperationPhrase}.` +
    '\n' +
    'Podrás rescatar en el siguiente día hábil.'

  useEffect(() => {
    let mounted = true
    const load = async () => {
      if (isWithinHours) {
        await Promise.all([
          dispatch(getUserBalance()),
          dispatch(getFund()),
          isRescue ? dispatch(getHoldingAccount()) : Promise.resolve()
        ])
      }
      if (mounted) setInitialLoading(false)
    }
    load()
    return () => {
      mounted = false
    }
  }, [dispatch, isWithinHours, isRescue])

  if (!isWithinHours) {
    return <InvestmentsOutOfService action={isRescue ? InvestmentActionType.RESCUE : InvestmentActionType.INVEST} />
  }
  const needsHolding = isRescue
  const holdingLoaded = needsHolding ? !!balanceHoldingAccount : true
  const showSkeleton = initialLoading || isLoading || !fund || !holdingLoaded
  if (showSkeleton) return <InvestmentOrderSkeleton />

  const handleTermsDownload = async () => {
    try {
      setLoadingTerms(true)
      const response = await InvestmentServices.getTermsheet()
      const base64Data = response.data.data

      const byteCharacters = atob(base64Data)
      const byteNumbers = new Array(byteCharacters.length)
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i)
      }
      const byteArray = new Uint8Array(byteNumbers)
      const blob = new Blob([byteArray], { type: 'application/pdf' })

      const url = window.URL.createObjectURL(blob)

      window.open(url, '_blank')
    } catch (error) {
      console.error('Error on download document:', error)
    } finally {
      setLoadingTerms(false)
    }
  }

  const currentBalance = isRescue ? balanceHoldingAccount : balance
  const balanceToNumber = +currentBalance.replace(/[.]/g, '').replace(',', '.')
  const availableBalanceRaw = isRescue ? balanceToNumber - pendingRescueAmount : balanceToNumber
  const availableBalanceCalculated = Number(availableBalanceRaw.toFixed(2))
  const availableBalance = availableBalanceCalculated < 0 ? 0 : availableBalanceCalculated

  const validateAmount = (value: string) => {
    const maxTwoDecimals = /^(\d?)+(\.?,?[0-9]{0,2}$)?$/
    const numericValue = +value.replace(',', '')
    const higherAmountWithComma = numericValue > (isRescue ? availableBalance : balanceToNumber)
    const lowerThanMinimum = value && numericValue < minimumAmount

    if (!maxTwoDecimals.test(value) && value.length > 1) {
      setErrorMessage(FORMAT_ERROR)
      setHasError(true)
    } else if (lowerThanMinimum && !isRescue) {
      setErrorMessage(LOWER_ERROR)
      setHasError(true)
    } else if (higherAmountWithComma) {
      setErrorMessage(HIGHER_ERROR)
      setHasError(true)
    } else {
      setHasError(false)
      setErrorMessage('')
    }
  }

  const formatAmount = (number?: string | number) => {
    if (!number || number === '' || number === '0') {
      return ['0', '00']
    }
    const float = `${number}`.replace(/[,]/g, '.')
    const values = float.split('.')

    const integerPart = values[0] || '0'
    const decimalPart = values.length > 1 ? values[1] : '00'

    return [
      Number(parseFloat(integerPart) || 0).toLocaleString('es-AR'),
      decimalPart
    ]
  }

  const checkAmountEndsWith = (value: string) => ({
    separator: value.endsWith('.'),
    pointNumber: /\.\d$/.test(value)
  })

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value

    const numericRegex = /^[0-9.,]*$/
    if (!numericRegex.test(inputValue)) {
      return
    }

    if (inputValue.length > 10) return

    const values = inputValue.split('.')
    if (values.length > 1 && values[1].length >= 3) {
      return
    }

    setAmount(inputValue)
    validateAmount(inputValue)
  }

  const replacedValue = amount.replace(',', '.') || '0'
  const numericAmount = Number(replacedValue) || 0

  const [integer, decimal] = formatAmount(replacedValue)
  const { separator, pointNumber } = checkAmountEndsWith(amount)

  const handleInvestmentSubmit = () => {
    if (isRegulationAccepted && !hasError && numericAmount > 0) {
      const accountNumber = investmentClient?.Client?.Accounts?.[0]?.number || ''
      const investment = {
        date: new Date().toISOString(),
        amount: numericAmount.toString(),
        account: accountNumber,
        type: isRescue ? 'RESCUE' : 'SUBSCRIPTION',
        statusId: 2
      }
      const isTotalRescueFlag = isRescue && Math.abs(numericAmount - availableBalance) < 0.01

      navigate('/content/investments/investment-operation/detail', {
        state: {
          title: isRescue ? 'Rescates' : 'Suscripciones',
          hasBackArrow: true,
          investment,
          fromInvestmentBalance: true,
          isTotalRescue: isTotalRescueFlag
        }
      })
    }
  }

  const handleRescueAll = () => {
    if (isRescue) {
      const fullString = availableBalance.toFixed(2)
      setAmount(fullString)
      validateAmount(fullString)
    }
  }

  const disclaimerTextPart1 = 'Se informa a los usuarios que Consultatio Asset Management Gerente de Fondos ' +
    'Comunes de Inversión S.A. actuará como Sociedad Gerente y Banco de Valores S.A. actuará como ' +
    'Sociedad Depositaria del Fondo. Adicionalmente, NASINI S.A. actuará como Agente de Colocación ' +
    'y Distribución Integral de Fondos Comunes de Inversión del mismo. El valor de la cuotaparte del ' +
    'Fondo será neto de gastos ordinarios de gestión y de los honorarios de la Sociedad Gerente y la ' +
    'Sociedad Depositaria. Los honorarios, gastos y demás información relevante se encuentran ' +
    'disponibles en '

  const linkUrl = 'https://www.consultatioinvestments.com/productos/fondos/st-zero-fci'

  const disclaimerTextPart2 = ' ALZ MERCADOS ' +
    'S.A. y su aplicación QIRA PAGOS únicamente brindan un canal tecnológico para la suscripción y ' +
    'rescate de cuotapartes del Fondo gestionado por Consultatio Investments S.A. y distribuido por ' +
    'NASINI S.A. Bajo ninguna circunstancia podrá interpretarse que ALZ MERCADOS S.A. coloca, ' +
    'comercializa, administra o gestiona el Fondo, ni que interviene en la toma de decisiones de ' +
    'inversión o en la custodia de los activos del mismo. Las inversiones en cuotapartes del Fondo no ' +
    'constituyen depósitos en Banco de Valores S.A. en los términos de la Ley de Entidades ' +
    'Financieras, ni cuentan con garantías de ningún tipo. Asimismo, Banco de Valores S.A. no asume ' +
    'compromiso alguno en cuanto al mantenimiento del valor del capital invertido, la rentabilidad del ' +
    'Fondo o la liquidez de las cuotapartes. Finalmente, se deja constancia de que la inversión en ' +
    'cuotapartes de Fondos Comunes de Inversión está sujeta a los riesgos del mercado financiero, y ' +
    'que bajo ninguna circunstancia se garantiza resultado de inversión alguno. Cualquier proyección ' +
    'basada en rendimientos pasados no constituye garantía de rentabilidad futura.'

  return (
    <MainContainer>
      <TitleContainer>
        <CustomText
          size="mmbig"
          weight="mediumfont"
          customStyle={styles.titleText}
        >
          {isRescue ? 'Rescatar a tu saldo:' : 'Suscribir desde tu saldo:'}
        </CustomText>
      </TitleContainer>

      <AlertContainer>
        <WarningIcon />
        <CustomText
          size="small"
          weight="semibold"
          customStyle={styles.alertText}
        >
          Revisar contra oferta
        </CustomText>
      </AlertContainer>

      <ContentContainer>
        <BalanceSection>
          <BalanceInfo>
            {isRescue
              ? (
                <>
                  <CustomText
                    size="mmbig"
                    weight="mediumfont"
                    customStyle={{
                      fontFamily: 'Poppins',
                      fontWeight: 500,
                      fontSize: 16,
                      lineHeight: '18px',
                      textAlign: 'center',
                      color: grayText
                    }}
                  >
                    {`Saldo invertido: $${balanceToNumber.toLocaleString(
                      'es-AR',
                      { minimumFractionDigits: 2, maximumFractionDigits: 2 }
                    )}`}
                  </CustomText>
                  <CustomText
                    size="mmbig"
                    weight="mediumfont"
                    customStyle={{
                      fontFamily: 'Poppins',
                      fontWeight: 500,
                      fontSize: 16,
                      lineHeight: '18px',
                      textAlign: 'center',
                      color: purpleDark
                    }}
                  >
                    {`Disponible para rescatar: $${availableBalance.toLocaleString(
                      'es-AR',
                      { minimumFractionDigits: 2, maximumFractionDigits: 2 }
                    )}`}
                  </CustomText>
                </>
                )
              : (
                <CustomText
                  size="big"
                  weight="mediumfont"
                  customStyle={styles.balanceText}
                >
                  {'Saldo disponible '}
                  <CustomText
                    size="big"
                    weight="semibold"
                    customStyle={{ color: purpleDark, fontWeight: 600, display: 'inline' }}
                  >
                    {'QIRA: '}
                    $
                    {availableBalance.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </CustomText>
                </CustomText>
                )}
          </BalanceInfo>

          <div style={{ marginTop: 14, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <AmountInputSection style={{ gap: 14 }}>
              <div style={{ position: 'relative', width: 'fit-content', margin: '0 auto' }}>
                <AmountDisplay
                  type="text"
                  value={amount ? amount.replace(/[,]/g, '.') : ''}
                  onChange={handleAmountChange}
                  placeholder="0"
                  maxLength={10}
                  style={{
                    opacity: 0,
                    position: 'absolute',
                    zIndex: 1,
                    width: '100%',
                    height: '100%',
                    border: 'none',
                    background: 'transparent',
                    fontFamily: 'Poppins',
                    fontSize: '52px',
                    fontWeight: '500',
                    textAlign: 'center'
                  }}
                />
                <div
                  style={{
                    position: 'relative',
                    zIndex: 0,
                    fontFamily: 'Poppins',
                    fontSize: '52px',
                    fontWeight: '500',
                    textAlign: 'center',
                    padding: '12px',
                    minHeight: '48px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    pointerEvents: 'none',
                    color: '#1A1A1A'
                  }}
                >
                  $
                  {integer}
                  {(!!decimal.length || separator)
                    ? (
                      <>
                        ,
                        {decimal || '0'}
                        {(separator || pointNumber) && '0'}
                      </>
                      )
                    : ',00'}
                </div>
              </div>
            </AmountInputSection>
            {isRescue && numericAmount !== availableBalance && availableBalance > 0 && (
            <RescueAllButton type="button" onClick={handleRescueAll} style={{ marginTop: 16 }}>
              Rescatar total
            </RescueAllButton>
            )}

            <CustomText
              size="mmbig"
              weight="regular"
              customStyle={{
                color: hasError && numericAmount > 0 ? red : styles.instructionText.color,
                textAlign: 'center',
                marginTop: '16px',
                fontFamily: 'Poppins',
                fontStyle: 'normal',
                fontSize: '16px',
                lineHeight: '154.7%',
                letterSpacing: '0.01em',
                whiteSpace: 'pre-line'
              }}
            >
              {numericAmount === 0
                ? `Ingresá el monto a ${isRescue ? 'rescatar' : 'suscribir'}.`
                : hasError
                  ? errorMessage
                  : isRescue
                    ? rescueInstruction
                    : subscriptionInstruction}
            </CustomText>
          </div>
        </BalanceSection>

        <DisclaimerSection>
          <DisclaimerText>
            <CustomText
              size="small"
              weight="regular"
              align="left"
              customStyle={{ fontSize: '12px' }}
            >
              {disclaimerTextPart1}
              <CustomText
                size="small"
                weight="regular"
                align="left"
                customStyle={{
                  fontSize: '12px',
                  color: purple,
                  textDecoration: 'underline',
                  display: 'inline'
                }}
              >
                <a
                  href={linkUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    color: purple,
                    textDecoration: 'underline'
                  }}
                >
                  {linkUrl}
                </a>
              </CustomText>
              {disclaimerTextPart2}
            </CustomText>
          </DisclaimerText>

          <CheckboxSection>
            <CheckboxContainer>
              <Checkbox
                type="checkbox"
                checked={isRegulationAccepted}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setIsRegulationAccepted(e.target.checked)}
              />
              <span>He leído y acepto el Reglamento del fondo.</span>
              <span
                onClick={() => {
                  if (!loadingTerms) {
                    handleTermsDownload()
                  }
                }}
                style={{
                  cursor: loadingTerms ? 'default' : 'pointer',
                  opacity: loadingTerms ? 0.6 : 1,
                  pointerEvents: loadingTerms ? 'none' : 'auto'
                }}
              >
                <CustomText
                  size="mmbig"
                  weight="mediumfont"
                  customStyle={styles.checkboxText}
                >
                  {loadingTerms ? 'Descargando...' : 'Descargar PDF'}
                </CustomText>
              </span>
            </CheckboxContainer>

            <ButtonSection>
              <SubmitButton
                type="button"
                onClick={handleInvestmentSubmit}
                disabled={!isRegulationAccepted || hasError || numericAmount === 0}
                className={isRegulationAccepted && !hasError && numericAmount > 0 ? 'enabled' : ''}
              >
                {isRescue ? InvestmentActionType.RESCUE : InvestmentActionType.INVEST}
              </SubmitButton>
            </ButtonSection>
          </CheckboxSection>
        </DisclaimerSection>
      </ContentContainer>
    </MainContainer>
  )
}

export default OperationInvestment
